import MultiviewService from "@/services/MultiviewService";
import router from "@/router";
import _ from "lodash";

const normalizeMultiview = (multiview) => {
  const normalized = _.cloneDeep(multiview || {});
  const layout = Number(normalized.layout);
  if (Array.isArray(normalized.streams) && Number.isInteger(layout) && layout >= 0) {
    normalized.streams = Array.from(
      { length: layout },
      (_item, index) => normalized.streams[index] || null
    );
  }
  return normalized;
};

const defaultState = () => ({
  multiviews: [],
  multiview: {},
  currentMultiviewId: "",
  multiviewLoaded: false,
  teamPermissionError: false,
  isLoading: false,
  isLoaded: false,
});

const state = defaultState();

const mutations = {
  SET_LOADING(state, isLoading) {
    state.isLoading = isLoading;
  },
  GET_MULTIVIEWS(state, multiviews) {
    state.multiviews = multiviews;
    state.isLoaded = true;
    state.isLoading = false;
  },
  GET_MULTIVIEW(state, multiview) {
    state.multiview = multiview;
    state.multiviewLoaded = true;
  },
  SET_CURRENT_MULTIVIEW(state, id) {
    state.currentMultiviewId = id;
  },
  SET_MULTIVIEW_SOURCE_STREAM(state, { id, index, stream }) {
    const multiviewId = id || state.multiview._id;
    const listed = state.multiviews.find((multiview) => multiview._id === multiviewId);
    if (listed) {
      if (!Array.isArray(listed.streams)) listed.streams = [];
      listed.streams[index] = stream;
    }
    if (state.multiview._id === multiviewId) {
      if (!Array.isArray(state.multiview.streams)) state.multiview.streams = [];
      state.multiview.streams[index] = stream;
    }
  },
  REMOVE_MULTIVIEW(state, id) {
    const objIndex = state.multiviews.findIndex(obj => { 
      return obj._id === id
    })
    if (objIndex >= 0) {
      state.multiviews.splice(objIndex, 1)
    }
  },
  TOGGLE_MULTIVIEW(state, id) {
    const objIndex = state.multiviews.findIndex(obj => { 
      return obj._id === id
    })

    if (objIndex >= 0) {
      Object.assign(state.multiviews[objIndex], { enabled: !state.multiviews[objIndex].enabled })
      state.multiview.enabled = !state.multiview.enabled
    }
  },
  SET_MULTIVIEW_LAYOUT(state, { id, streams, layout }) {
    const objIndex = state.multiviews.findIndex(obj => { 
      return obj._id === id
    })

    if (objIndex >= 0) {
      Object.assign(state.multiviews[objIndex], { layout, streams })
      state.multiview.layout = layout
      state.multiview.streams = streams
    }
  },
  SET_MULTIVIEW_NAME(state, { id, name }) {
    const objIndex = state.multiviews.findIndex(obj => { 
      return obj._id === id
    })

    if (objIndex >= 0) {
      Object.assign(state.multiviews[objIndex], { name })
      state.multiview.name = name
    }
  },
  SET_MULTIVIEW_TEAM_PERMISSION_ERROR(state, { perm }) {
    state.teamPermissionError = perm;
  },
};

const actions = {
  async getMultiviews({ commit, state }, forceRefresh = false) {
    if (state.isLoaded && !forceRefresh) {
      return state.multiviews;
    }

    if (state.isLoading) {
      return new Promise((resolve) => {
        const checkLoading = setInterval(() => {
          if (!state.isLoading) {
            clearInterval(checkLoading);
            resolve(state.multiviews);
          }
        }, 50);
      });
    }

    commit("SET_LOADING", true);
    try {
      const multiviews = await MultiviewService.getMultiviews();
      commit("GET_MULTIVIEWS", multiviews);
      return multiviews;
    } catch (error) {
      commit("SET_LOADING", false);
      throw error;
    }
  },
  seedMultiview({ commit, state }, id) {
    const listed = state.multiviews.find((multiview) => multiview._id === id);
    if (!listed) return false;
    commit("GET_MULTIVIEW", normalizeMultiview(listed));
    return true;
  },
  async fetchMultiview(_context, id) {
    return normalizeMultiview(await MultiviewService.getMultiview(id));
  },
  applyMultiview({ commit }, multiview) {
    commit("GET_MULTIVIEW", normalizeMultiview(multiview));
  },
  async getMultiview({ dispatch }, id) {
    const multiview = await dispatch("fetchMultiview", id);
    dispatch("applyMultiview", multiview);
    return multiview;
  },
  setSource({ commit }, { id, index, stream }) {
    commit("SET_MULTIVIEW_SOURCE_STREAM", { id, index, stream })
  },
  setCurrentMultiview({ commit }, id) {
    commit("SET_CURRENT_MULTIVIEW", id);
  },
  removeMultiview({ commit }, { id }) {
    commit("REMOVE_MULTIVIEW", id)
  },
  toggleMultiview({ commit }, { id }) {
    commit("TOGGLE_MULTIVIEW", id)
  },
  setLayout({ commit }, { id, streams, layout }) {
    commit("SET_MULTIVIEW_LAYOUT", { id, streams, layout })
  },
  setName({ commit }, { id, name }) {
    commit("SET_MULTIVIEW_NAME", { id, name })
  },
  setTeamPermissionError({ commit }, { error }) {
    commit("SET_MULTIVIEW_TEAM_PERMISSION_ERROR", { perm: error })
  },
};

const getters = {
  multiviews: (state) => state.multiviews,
  multiview: (state) => state.multiview,
  currentMultiview: (state) => {
    if (state.currentMultiviewId === '') {
      return state.multiviews.find(
        (mv) =>
          mv._id === (router.currentRoute.params && router.currentRoute.params.multiviewId)
      );
    } else {
      return state.multiviews.find((mv) => mv._id === state.currentMultiviewId);
    }
  },
  multiviewLoaded: (state) => state.multiviewLoaded,
  teamPermissionError: (state) => state.teamPermissionError,
  isLoaded: (state) => state.isLoaded,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
